import React, { useEffect, useRef, useState } from 'react';

/**
 * Lightweight Cloudflare Turnstile widget.
 * If no siteKey is provided OR Turnstile is disabled, renders nothing and
 * lets the parent treat onToken('') as "no challenge required".
 *
 * Loads the turnstile script once. Calls onToken(token) when the user
 * passes the challenge.
 *
 * For testing, Cloudflare provides the always-pass site key:
 *   1x00000000000000000000AA
 */
let SCRIPT_PROMISE = null;

const loadScript = () => {
  if (SCRIPT_PROMISE) return SCRIPT_PROMISE;
  SCRIPT_PROMISE = new Promise((resolve, reject) => {
    if (typeof window === 'undefined') return reject(new Error('window undefined'));
    if (window.turnstile) return resolve(window.turnstile);
    const s = document.createElement('script');
    s.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
    s.async = true;
    s.defer = true;
    s.onload = () => resolve(window.turnstile);
    s.onerror = () => reject(new Error('Failed to load Turnstile'));
    document.head.appendChild(s);
  });
  return SCRIPT_PROMISE;
};

const Turnstile = ({ siteKey, onToken }) => {
  const ref = useRef(null);
  const widgetIdRef = useRef(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!siteKey) return;
    let mounted = true;
    loadScript()
      .then((ts) => {
        if (!mounted || !ref.current || !ts) return;
        widgetIdRef.current = ts.render(ref.current, {
          sitekey: siteKey,
          theme: 'dark',
          callback: (token) => onToken && onToken(token),
          'error-callback': () => onToken && onToken(''),
          'expired-callback': () => onToken && onToken(''),
          'timeout-callback': () => onToken && onToken(''),
        });
      })
      .catch((e) => setError(e.message));
    return () => {
      mounted = false;
      if (window.turnstile && widgetIdRef.current) {
        try { window.turnstile.remove(widgetIdRef.current); }
        catch (err) { console.warn('Turnstile cleanup failed:', err); }
      }
    };
  }, [siteKey, onToken]);

  if (!siteKey) return null;
  return (
    <div className="my-3" data-testid="turnstile-widget">
      <div ref={ref} />
      {error && <p className="text-xs text-rose-400 mt-1">{error}</p>}
    </div>
  );
};

export default Turnstile;
